import { useRef, useState } from "react";
import { UsersAPI, type UserDto } from "../../api/usersApi";
import { Avatar, Banner, ROLES, Section, errText, fmtBirth, fmtPhone } from "./cabinet-ui";

type Msg = { ok: boolean; text: string } | null;
const toDateInput = (v?: string | null) => (String(v ?? "").match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? "");
const emailOk = (v: string) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function ProfileTab({ user, onChanged }: { user: UserDto; onChanged: () => Promise<void> | void }) {
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState({
    lastName: user.lastName ?? "", firstName: user.firstName ?? "", middleName: user.middleName ?? "",
    birthDate: toDateInput(user.birthDate), phone: user.phone ?? "", internalPhone: user.internalPhone ?? "", emailPersonal: user.emailPersonal ?? "",
  });
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const startEdit = () => {
    setForm({
      lastName: user.lastName ?? "", firstName: user.firstName ?? "", middleName: user.middleName ?? "",
      birthDate: toDateInput(user.birthDate), phone: user.phone ?? "", internalPhone: user.internalPhone ?? "", emailPersonal: user.emailPersonal ?? "",
    });
    setMsg(null); setEditing(true);
  };

  const save = async () => {
    if (!form.lastName.trim() || !form.firstName.trim()) { setMsg({ ok: false, text: "Укажите фамилию и имя" }); return; }
    if (!emailOk(form.emailPersonal.trim())) { setMsg({ ok: false, text: "Личная почта указана неверно" }); return; }
    setBusy(true); setMsg(null);
    try {
      await UsersAPI.updateMe({
        lastName: form.lastName.trim(), firstName: form.firstName.trim(),
        middleName: form.middleName.trim() || undefined,
        birthDate: form.birthDate || undefined,
        phone: form.phone.trim() || undefined, internalPhone: form.internalPhone.trim() || undefined,
        emailPersonal: form.emailPersonal.trim() || undefined,
      } as any);
      await onChanged();
      setEditing(false);
      setMsg({ ok: true, text: "Профиль сохранён" });
    } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  };

  const pickPhoto = async (file?: File | null) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) { setMsg({ ok: false, text: "Загрузите изображение JPG, PNG, WebP или GIF" }); return; }
    if (file.size > 5 * 1024 * 1024) { setMsg({ ok: false, text: "Файл больше 5 МБ" }); return; }
    setBusy(true); setMsg(null);
    try { await UsersAPI.uploadAvatar(file); await onChanged(); setMsg({ ok: true, text: "Фото обновлено" }); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <>
      {msg && <Banner kind={msg.ok ? "ok" : "err"} onClose={() => setMsg(null)}>{msg.text}</Banner>}
      <div className="cb-profile">
        <div className="cb-profile-photo">
          <Avatar url={user.avatarUrl} first={user.firstName} last={user.lastName} size={112} />
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={(e) => pickPhoto(e.target.files?.[0])} />
          <button type="button" className="cb-btn ghost" disabled={busy} onClick={() => fileRef.current?.click()}>Сменить фото</button>
        </div>

        {!editing ? (
          <div className="cb-profile-info">
            <dl>
              <dt>ФИО</dt><dd>{user.lastName} {user.firstName} {user.middleName}</dd>
              <dt>Роль</dt><dd>{ROLES[user.role] ?? user.role}</dd>
              <dt>Телефон</dt><dd>{fmtPhone(user.phone)}</dd>
              <dt>Внутренний номер</dt><dd>{user.internalPhone || "—"}</dd>
              <dt>Корп. почта (логин)</dt><dd>{user.emailCorporate || "—"}</dd>
              <dt>Личная почта</dt><dd>{user.emailPersonal || "—"}</dd>
              <dt>Дата рождения</dt><dd>{fmtBirth(user.birthDate)}</dd>
            </dl>
            <div className="cb-actions">
              <button type="button" className="cb-btn" onClick={startEdit}>Редактировать</button>
            </div>
          </div>
        ) : (
          <div className="cb-profile-info">
            <div className="cb-form-grid">
              <label>Фамилия<input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} /></label>
              <label>Имя<input value={form.firstName} onChange={(e) => set("firstName", e.target.value)} /></label>
              <label>Отчество<input value={form.middleName} onChange={(e) => set("middleName", e.target.value)} /></label>
              <label>Дата рождения<input type="date" value={form.birthDate} onChange={(e) => set("birthDate", e.target.value)} /></label>
              <label>Телефон<input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+7 ..." /></label>
              <label>Внутренний номер<input value={form.internalPhone} onChange={(e) => set("internalPhone", e.target.value)} /></label>
              <label className="wide">Личная почта<input type="email" value={form.emailPersonal} onChange={(e) => set("emailPersonal", e.target.value)} /></label>
            </div>
            <div className="cb-hint">Роль и корпоративную почту (логин) меняет администратор.</div>
            <div className="cb-actions">
              <button type="button" className="cb-btn" disabled={busy} onClick={save}>{busy ? "Сохранение…" : "Сохранить"}</button>
              <button type="button" className="cb-btn ghost" disabled={busy} onClick={() => setEditing(false)}>Отмена</button>
            </div>
          </div>
        )}
      </div>

      <PasswordCard />
    </>
  );
}

function PasswordCard() {
  const [open, setOpen] = useState(false);
  const [oldPass, setOld] = useState("");
  const [newPass, setNew] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);

  const close = () => { setOpen(false); setOld(""); setNew(""); setRepeat(""); };
  const save = async () => {
    if (!oldPass) { setMsg({ ok: false, text: "Введите текущий пароль" }); return; }
    if (newPass.length < 8) { setMsg({ ok: false, text: "Новый пароль должен быть не короче 8 символов" }); return; }
    if (newPass !== repeat) { setMsg({ ok: false, text: "Пароли не совпадают" }); return; }
    if (newPass === oldPass) { setMsg({ ok: false, text: "Новый пароль совпадает с текущим" }); return; }
    setBusy(true); setMsg(null);
    try {
      await UsersAPI.changePassword(oldPass, newPass);
      close();
      setMsg({ ok: true, text: "Пароль изменён. В следующий раз входите с новым паролем." });
    } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  };

  return (
    <Section title="Безопасность">
      {msg && <Banner kind={msg.ok ? "ok" : "err"} onClose={() => setMsg(null)}>{msg.text}</Banner>}
      {!open ? (
        <button type="button" className="cb-btn ghost" onClick={() => { setMsg(null); setOpen(true); }}>Сменить пароль</button>
      ) : (
        <div className="cb-form">
          <div className="cb-form-grid">
            <label>Текущий пароль<input type="password" autoComplete="current-password" value={oldPass} onChange={(e) => setOld(e.target.value)} /></label>
            <label>Новый пароль (от 8 символов)<input type="password" autoComplete="new-password" value={newPass} onChange={(e) => setNew(e.target.value)} /></label>
            <label>Повторите новый пароль<input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} /></label>
          </div>
          <div className="cb-actions">
            <button type="button" className="cb-btn" disabled={busy} onClick={save}>{busy ? "Сохранение…" : "Сохранить пароль"}</button>
            <button type="button" className="cb-btn ghost" disabled={busy} onClick={close}>Отмена</button>
          </div>
        </div>
      )}
    </Section>
  );
}
